import { ListPage } from "@services/common/types";
import API from "@services/root/rootApi";

import {
  Ticket,
  TicketAdapter,
  TicketAttachment,
  TicketPayload,
  TicketsCount,
  TicketsCounters,
  TicketsFilters,
} from "./types";
import { Dispatch, SetStateAction } from "react";

export const getTicketsCounters = async (
  filters?: TicketsFilters
): Promise<TicketsCounters> => {
  let response = await API.post<TicketsCounters>(
    `/symphony-api/ticket/counters`,
    filters
  );
  return response.data;
};

export const getTickets = async (
  startIndex?: number,
  totalLimit?: number,
  maxResults?: number,
  startDate?: number,
  endDate?: number,
  orderBy?: string,
  filters?: TicketsFilters
): Promise<ListPage<Ticket>> => {
  const params: any = {
    startIndex,
    totalLimit,
    maxResults,
    startDate,
    endDate,
    orderBy,
  };

  const response = await API.post<ListPage<Ticket>>(
    `/symphony-api/ticket`,
    filters,
    {
      params,
    }
  );
  return response.data;
};

export const getTicketsCount = async (
  startDate?: number,
  endDate?: number,
  filters?: TicketsFilters
): Promise<TicketsCount> => {
  const params: any = {
    startDate,
    endDate,
  };
  if (filters) {
    delete filters["start.after"];
    delete filters["end.before"];
  }

  const response = await API.post<TicketsCount>(
    `/symphony-api/ticket/count`,
    filters,
    {
      params,
    }
  );
  return response.data;
};

export const getTicket = async (id: string): Promise<Ticket> => {
  const response = await API.get<Ticket>(`/symphony-api/ticket/${id}`);
  response.data?.details?.events?.sort(function (a, b) {
    return a.createDate - b.createDate;
  });
  return response.data;
};

export const putTicket = async (ticket: Ticket): Promise<string> => {
  const params: any = {
    ticket,
  };

  const response = await API.put<string>(
    `/symphony-api/ticket${ticket.id !== undefined ? `/${ticket.id}` : ""}`,
    ticket
  );

  return response.data;
};

export const closeTickets = async ({
  closeAll,
  startDate,
  endDate,
  ...body
}: {
  parentId: string | string[];
  parentEntityClass: string;
  closeAll?: boolean;
  ticketsIds?: number[];
  resolution: string;
  source: string;
  comment?: string;
  startDate?: number;
  endDate?: number;
  filters?: TicketsFilters;
}) =>
  await API.post(`/symphony-api/ticket/close`, body, {
    params: {
      closeAll,
      startDate,
      endDate,
    },
  });

export const deleteAttachment = async (id: TicketAttachment["id"]) =>
  await API.delete(`/symphony-api/ticket/attachment/${id}`);

export const addAttachment = async (
  data: FormData,
  filename: string,
  ticketId: number,
  email: string,
  setProgress: Dispatch<SetStateAction<number>>
) =>
  await API.post(
    `/symphony-api/ticket/attachment?name=${encodeURIComponent(
      filename
    )}&ticketId=${ticketId}&creator=${email}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const progress = (progressEvent.progress ?? 0) * 100;
        setProgress(progress);
      },
    }
  );

export const getTicketAdapter = async (accountId?: string) => {
  const response = await API.get<TicketAdapter>(
    "/symphony-api/ticket/adapter/config",
    {
      params: {
        accountId,
      },
    }
  );
  return response.data;
};

export const updateTicketAdapter = async (
  adapter: TicketAdapter,
  accountId?: string
) =>
  await API.put("/symphony-api/ticket/adapter/config", adapter, {
    params: {
      accountId,
    },
  });

export const getTicketAttachment = async (id: number) => {
  const response = await API.get(`/symphony-api/ticket/attachment/${id}`, {
    responseType: "blob",
  });
  return response.data;
};

export const testSendMessage = async (accountId: string) => {
  const response = await API.post(
    `/symphony-api/ticket/adapter/${accountId}/test`
  );
  return response.data;
};

export const getTicketPayload = async (): Promise<TicketPayload[]> => {
  const response = await API.get(
    `/symphony-api/ticket/adapter/webhooks/variables`
  );
  return response.data;
};

export const getTicketPayloadTemplate = async () => {
  const response = await API.get<string>('/symphony-api/ticket/adapter/webhooks/default-template');

  return response.data;
};
